import { useState, useEffect } from 'react';
import Sidebar from '@/pages/dashboard/components/Sidebar';
import NotesPanel from '@/pages/dashboard/components/NotesPanel';

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileNotesOpen, setMobileNotesOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setSidebarCollapsed(true);
      setNotesCollapsed(true);
    }
  }, [isMobile]);

  const leftOffset = isMobile ? 0 : sidebarCollapsed ? 68 : 240;
  const rightOffset = isMobile ? 0 : notesCollapsed ? 0 : 280;

  return (
    <div className="min-h-screen bg-background-50">
      {!isMobile && (
        <>
          <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
          <NotesPanel collapsed={notesCollapsed} onToggle={() => setNotesCollapsed(!notesCollapsed)} />
        </>
      )}

      {isMobile && mobileMenuOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="relative z-10 w-[240px] h-full">
            <Sidebar collapsed={false} onToggle={() => setMobileMenuOpen(false)} />
          </div>
        </div>
      )}

      {isMobile && mobileNotesOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setMobileNotesOpen(false)}
          />
          <div className="relative z-10 w-[280px] h-full">
            <NotesPanel collapsed={false} onToggle={() => setMobileNotesOpen(false)} />
          </div>
        </div>
      )}

      <div
        className="transition-all duration-300 min-h-screen"
        style={{
          marginLeft: `${leftOffset}px`,
          marginRight: `${rightOffset}px`,
        }}
      >
        {isMobile && (
          <header className="sticky top-0 z-20 flex items-center justify-between h-14 px-4 bg-background-50 border-b border-background-200">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer"
              >
                <i className="ri-menu-line text-lg" />
              </button>
              <div className="w-7 h-7 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className="ri-building-4-line text-sm text-background-50" />
              </div>
              <span className="font-heading font-semibold text-sm text-foreground-900">ConstruCRM</span>
            </div>
            <button
              onClick={() => setMobileNotesOpen(true)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-600 cursor-pointer relative"
            >
              <i className="ri-sticky-note-line text-lg" />
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50 text-[9px] font-semibold px-1">5</span>
            </button>
          </header>
        )}

        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}